import { useState } from 'react';
import clsx from 'clsx';
import { Card } from '../../layout/Card.jsx';
import { Icon } from '../../layout/Icon.jsx';
import { useAuth } from '../../shared/useAuth.js';
import { useAllowedWarehouses } from '../../shared/warehouseAccess.js';
import { useWarehouses } from '../products/hooks/useCategories.js';

import { InventoryKpi } from './components/InventoryKpi.jsx';
import { StockTable } from './components/StockTable.jsx';
import { MovementsTable } from './components/MovementsTable.jsx';
import { AdjustModal } from './components/AdjustModal.jsx';
import { LowStockPanel } from './components/LowStockPanel.jsx';
import { useStock } from './hooks/useStock.js';
import { useMovements } from './hooks/useMovements.js';

const ALL_TABS = [
  { key: 'stock', label: 'المخزون', permission: null },
  { key: 'movements', label: 'حركة المخزون', permission: 'inventory.view' },
  { key: 'low', label: 'مخزون منخفض', permission: 'inventory.view' }
];

const MOVEMENT_TYPES = [
  { key: '', label: 'الكل' },
  { key: 'purchase', label: 'شراء' },
  { key: 'sale', label: 'بيع' },
  { key: 'adjustment', label: 'تسوية' },
  { key: 'damage', label: 'تالف' },
  { key: 'return', label: 'مرتجع' },
  { key: 'transfer', label: 'نقل' }
];

export default function InventoryScreen() {
  const hasPermission = useAuth((s) => s.hasPermission);
  const canAdjust = hasPermission('inventory.adjust');
  const TABS = ALL_TABS.filter((t) => !t.permission || hasPermission(t.permission));

  const [tab, setTab] = useState('stock');
  const { data: rawWarehouses = [] } = useWarehouses();
  const warehouses = useAllowedWarehouses(rawWarehouses);

  // Stock filters
  const [search, setSearch] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [lowOnly, setLowOnly] = useState(false);
  const [stockPage, setStockPage] = useState(1);

  // Movements filters
  const [moveType, setMoveType] = useState('');
  const [moveWarehouseId, setMoveWarehouseId] = useState('');
  const [moveFrom, setMoveFrom] = useState('');
  const [moveTo, setMoveTo] = useState('');
  const [movePage, setMovePage] = useState(1);

  const stockQuery = useStock({
    q: search || undefined,
    warehouseId: warehouseId || undefined,
    lowStock: lowOnly || undefined,
    page: stockPage,
    limit: 25
  });

  const movementsQuery = useMovements({
    type: moveType || undefined,
    warehouseId: moveWarehouseId || undefined,
    from: moveFrom || undefined,
    to: moveTo || undefined,
    page: movePage,
    limit: 30
  });

  const [adjustProduct, setAdjustProduct] = useState(null);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-extrabold tracking-tight m-0">المخزون</h2>
          <div className="text-ink-3 text-xs mt-0.5">عرض المخزون، حركاته، والتنبيهات على الكميات المنخفضة</div>
        </div>
      </div>

      <InventoryKpi />

      <Card>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-1 p-1 bg-paper-2 rounded-[10px]">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={clsx(
                  'px-3.5 py-1.5 rounded-[8px] text-[12.5px] font-semibold transition-colors',
                  tab === t.key ? 'bg-orange text-white shadow-orange-glow' : 'text-ink-2 hover:bg-white'
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'stock' ? (
            <>
              <div className="relative flex-1 min-w-[200px]">
                <Icon name="search" size={14} className="absolute top-1/2 -translate-y-1/2 right-3 text-ink-3 pointer-events-none" />
                <input
                  className="input pr-9 text-[12.5px]"
                  placeholder="بحث باسم المنتج، SKU، أو الباركود..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setStockPage(1); }}
                />
              </div>
              <select
                className="input text-[12.5px]"
                style={{ width: 180 }}
                value={warehouseId}
                onChange={(e) => { setWarehouseId(e.target.value); setStockPage(1); }}
              >
                <option value="">كل المخازن</option>
                {warehouses.map((w) => (
                  <option key={w._id || w.id} value={w._id || w.id}>{w.name}</option>
                ))}
              </select>
              <label className="flex items-center gap-2 cursor-pointer text-[12.5px] text-ink-2 px-3">
                <input
                  type="checkbox"
                  className="accent-orange"
                  checked={lowOnly}
                  onChange={(e) => { setLowOnly(e.target.checked); setStockPage(1); }}
                />
                المنخفضة فقط
              </label>
            </>
          ) : null}

          {tab === 'movements' ? (
            <>
              <select
                className="input text-[12.5px]"
                style={{ width: 140 }}
                value={moveType}
                onChange={(e) => { setMoveType(e.target.value); setMovePage(1); }}
              >
                {MOVEMENT_TYPES.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
              <select
                className="input text-[12.5px]"
                style={{ width: 160 }}
                value={moveWarehouseId}
                onChange={(e) => { setMoveWarehouseId(e.target.value); setMovePage(1); }}
              >
                <option value="">كل المخازن</option>
                {warehouses.map((w) => (
                  <option key={w._id || w.id} value={w._id || w.id}>{w.name}</option>
                ))}
              </select>
              <input
                className="input text-[12.5px]"
                type="date"
                style={{ width: 140 }}
                value={moveFrom}
                onChange={(e) => { setMoveFrom(e.target.value); setMovePage(1); }}
              />
              <input
                className="input text-[12.5px]"
                type="date"
                style={{ width: 140 }}
                value={moveTo}
                onChange={(e) => { setMoveTo(e.target.value); setMovePage(1); }}
              />
            </>
          ) : null}
        </div>
      </Card>

      {tab === 'stock' ? (
        <StockTable
          products={stockQuery.data?.items || []}
          loading={stockQuery.isLoading}
          page={stockQuery.data?.page || stockPage}
          pages={stockQuery.data?.pages || 1}
          total={stockQuery.data?.total || 0}
          onPageChange={(p) => setStockPage(Math.max(1, p))}
          onAdjust={(p) => setAdjustProduct(p)}
          canAdjust={canAdjust}
        />
      ) : null}

      {tab === 'movements' ? (
        <MovementsTable
          movements={movementsQuery.data?.items || []}
          loading={movementsQuery.isLoading}
          page={movementsQuery.data?.page || movePage}
          pages={movementsQuery.data?.pages || 1}
          total={movementsQuery.data?.total || 0}
          onPageChange={(p) => setMovePage(Math.max(1, p))}
        />
      ) : null}

      {tab === 'low' ? <LowStockPanel /> : null}

      <AdjustModal
        open={!!adjustProduct}
        product={adjustProduct}
        onClose={() => setAdjustProduct(null)}
      />
    </div>
  );
}
